import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { PurchaseTags } from '@shared/purchase-tags/service-level-tag.component';
import { Purchase, PurchaseStatus, ROLES_KEYS, User, UserProcessStatus } from '@interfaces';
import { ReplaySubject, Subject } from 'rxjs';
import { UserAssetsFacade } from '@state/user-assets';
import { AnalyticsService } from '@core/services/analytics.service';
import { AvailableLanguages } from '@constants';
import { PurchaseFacade } from '@state/purchase';
import { FileHandlerService } from '@core/services/file-handler.service';
import { takeUntil } from 'rxjs/operators';
import moment from 'moment';

@Component({
  selector: 'app-user-settings-transactions',
  templateUrl: './user-settings-transactions.component.html',
  styleUrls: ['./user-settings-transactions.component.scss'],
  standalone: false,
})
export class UserSettingsTransactionsComponent implements OnInit, OnDestroy {
  private readonly userAssets = inject(UserAssetsFacade);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly purchaseFacade = inject(PurchaseFacade);

  private destroy$: Subject<boolean> = new ReplaySubject(1);

  @Input() myself: User;
  @Input() currentLang: AvailableLanguages;

  public showPurchaseList = false;
  public purchaseList$ = this.purchaseFacade.purchaseList$;
  public loadingPurchaseList$ = this.purchaseFacade.loadingPurchaseList$;
  public PurchaseStatus = PurchaseStatus;
  public UserProcessStatus = UserProcessStatus;

  protected readonly PurchaseTags = PurchaseTags;

  public ngOnInit(): void {
    if (this.myself?.role.key === ROLES_KEYS.Foreigner && this.myself?.company.purchaseEnabled) {
      this.purchaseFacade.getMyPurchaseList();
    }

    this.purchaseFacade.getMyPurchaseListSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.showPurchaseList = true;
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public downloadPurchaseDocument(purchase: Partial<Purchase>): void {
    if (!purchase.purchaseDocumentId) {
      return;
    }

    const previewTargetKey = this.fileHandlerService.preparePreviewTarget(purchase.purchaseDocumentId);

    this.userAssets.downloadMyAsset(purchase.purchaseDocumentId, {
      previewTargetKey,
    });
    this.analyticsService.trackEvent('user_event', 'user_download_purchase_document');
  }

  // P24 returns 'YYYYMMDDHHmm' in Warsaw time
  public formatP24Date(date: string): string {
    return moment(date, 'YYYYMMDDHHmm').format('YYYY-MM-DD HH:mm');
  }
}
